import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Input, Modal, Row, Select, notification } from "antd";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import useListQuery from "hooks/useListQuery";
import BasicButtonPrimary from "components/BasicButtonPrimary";
import GiftOrderStatsWidgets from "./_components/GiftOrderStatsWidgets";
import GiftOrderTable from "./_components/GiftOrderTable";
import GiftOrderImportModal from "./_components/GiftOrderImportModal";
import OrderService from "services/order";
import ProductService from "services/product";
import { number_to_price } from "helper/common";
import { ExportFile } from "hooks/FileHandler";
import ModalProgress from "components/ModalProgress/ModalProgress";
import moment from "moment";
import "Page/Receipt/receipt.scss";
import { PRODUCT_PROVIDER, PRODUCT_CATEGORIES, stations_location } from "constants/order";
import addKeyLocalStorage from "helper/localStorage";
import { useSelector } from "react-redux";
import {
  validateEmail,
  validateGiftField,
  validatePhoneNumber,
  validateVehicleIdentity,
  getGiftLicensePlates,
  normalizePlate,
  sanitizePlateInput,
} from "helper/orderHelper";

const GIFT_ORDER_FILTER = {
  orderType: 2,
  orderCategory: 2,
};

const GIFT_ORDER_STATS_FILTER = {
  orderCategory: 2,
};
const CANCELED_GIFT_ORDER_STATUS = 20;
const GIFT_STATS_PAGE_SIZE = 100;
const EMAIL_SEARCH_PAGE_SIZE = 100;
const GIFT_PRODUCT_CATEGORIES = [2, 3, 4];
const GIFT_PRODUCT_ALLOWED_PROVIDERS = ["TAMOVE", "TTDK"];
const GIFT_ORDER_STATUS_TEXT = {
  0: "Đang chờ",
  10: "Đã xác nhận",
  20: "Đã huỷ",
  30: "Hoàn thành",
};
const GIFT_EXPORT_FIELDS = [
  { key: "orderId", label: "ID" },
  { key: "giftAmount", label: "Tổng tiền" },
  { key: "customerInfo", label: "Khách hàng" },
  { key: "giftType", label: "Loại quà tặng" },
  { key: "giftPackage", label: "Tên quà tặng" },
  { key: "orderStatusText", label: "Trạng thái" },
  { key: "createdAt", label: "Thời gian tạo" },
];

const GIFT_PRODUCT_PROVIDERS = Object.values(PRODUCT_PROVIDER)
  .filter((provider) => provider.providerCode === "ALL" || provider.productCategory?.some((category) => GIFT_PRODUCT_CATEGORIES.includes(category)))
  .map((provider) => ({
    label: provider.providerName,
    value: provider.providerCode,
  }));

const getDefaultGiftProductProvider = () => {
  const themeProvider = process.env.REACT_APP_THEME_NAME === "TAMOVE" ? "TAMOVE" : "TTDK";
  return GIFT_PRODUCT_PROVIDERS.some((provider) => provider.value === themeProvider)
    ? themeProvider
    : "TTDK";
};

const DEFAULT_GIFT_PRODUCT_PROVIDER = getDefaultGiftProductProvider();

const getDisplayAppsLabel = (displayApps) => !displayApps ? "" : `${displayApps}`
  .split(",")
  .map((item) => item.trim())
  .filter(Boolean)
  .join(", ");

const getProductOptionLabel = (product) => {
  const displayAppsLabel = getDisplayAppsLabel(product?.displayApps);
  const priceLabel = `${number_to_price(product?.productPrice || 0)} VNĐ`;
  return `${product?.productName || ""} | ${displayAppsLabel} | ${priceLabel}`;
};

const getGiftAmount = (order) => order?.orderItems?.reduce((sum, item) => sum + Number(item.payAmount || 0), 0) || 0;
const isEmailSearchText = (value) => /\S+@\S+\.\S+/.test(String(value || "").trim());
const GiftOrder = () => {
  const [form] = Form.useForm();
  const member = useSelector((state) => state.member || {});
  const [isGiftModalOpen, setIsGiftModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [giftProducts, setGiftProducts] = useState([]);
  const [giftStats, setGiftStats] = useState({});
  const [isGiftStatsLoading, setIsGiftStatsLoading] = useState(false);
  const [giftProductProvider, setGiftProductProvider] = useState(DEFAULT_GIFT_PRODUCT_PROVIDER);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importModalVersion, setImportModalVersion] = useState(0);
  const [isExportProgress, setIsExportProgress] = useState(false);
  const [exportPercent, setExportPercent] = useState(0);
  const { onExportExcel } = ExportFile();

  const getGiftOrders = useCallback(async (params = {}) => {
  const searchText = String(params?.searchText || "").trim();
  const isEmailSearch = isEmailSearchText(searchText);

  if (!isEmailSearch) {
    return OrderService.getList({
      ...params,
      ...(searchText ? { searchText } : {}),
      filter: {
        ...params?.filter,
        ...GIFT_ORDER_FILTER,
      },
    });
  }

  const {
    searchText: _searchText,
    filter: paramsFilter,
    skip = 0,
    limit = 10,
    ...restParams
  } = params;

  let fetchSkip = 0;
  let matchedOrders = [];

  while (true) {
    const response = await OrderService.getList({
      ...restParams,
      skip: fetchSkip,
      limit: EMAIL_SEARCH_PAGE_SIZE,
      filter: {
        ...paramsFilter,
        ...GIFT_ORDER_FILTER,
      },
    });

    const page = response?.data || [];

    matchedOrders = matchedOrders.concat(
      page.filter((order) => String(order?.email || "")
        .trim()
        .toLowerCase()
        .includes(searchText.toLowerCase()))
    );

    if (page.length < EMAIL_SEARCH_PAGE_SIZE) {
      break;
    }

    fetchSkip += EMAIL_SEARCH_PAGE_SIZE;
  }

  return {
    data: matchedOrders.slice(skip, skip + limit),
    total: matchedOrders.length,
  };
}, []);


  const {
    data,
    loading,
    pagination,
    filters,
    sorter,
    handleTableChange,
    refetch,
    handleSearch,
    hasMore,
    startDate,
    endDate,
    searchValue,
  } = useListQuery(getGiftOrders, "gift-orders", {
    defaultSorter: {
      key: "createdAt",
      value: "desc",
    },
    defaultStartDate: new Date(),
    defaultEndDate: new Date(),
  });

  const loadGiftProducts = useCallback(async () => {
    const filter = {
      productCategory: GIFT_PRODUCT_CATEGORIES,
      isGiftEnabled: 1,
    };

    const response = await ProductService.advanceUserGetList({
      filter,
      limit: 100,
    });
    setGiftProducts((response?.data || []).filter((product) => !product.productSKU?.toUpperCase().includes("UUDAI")));
  }, []);

  const loadGiftStats = useCallback(async () => {
    setIsGiftStatsLoading(true);

    const now = moment();
    const currentMonthStart = moment(now).startOf("month").format("YYYY-MM-DD HH:mm:ss");
    const currentMonthEnd = moment(now).endOf("month").format("YYYY-MM-DD HH:mm:ss");
    const prevMonthStart = moment(now).subtract(1, "month").startOf("month").format("YYYY-MM-DD HH:mm:ss");
    const prevMonthEnd = moment(now).subtract(1, "month").endOf("month").format("YYYY-MM-DD HH:mm:ss");

    try {
      const [currentMonthGiftStats, previousMonthGiftStats] = await Promise.all([
        OrderService.getOrderStatistics({
          filter: GIFT_ORDER_STATS_FILTER,
          startDate: currentMonthStart,
          endDate: currentMonthEnd,
        }),
        OrderService.getOrderStatistics({
          filter: GIFT_ORDER_STATS_FILTER,
          startDate: prevMonthStart,
          endDate: prevMonthEnd,
        }),
      ]);

      setGiftStats({
        currentMonthGiftAmount: Number(currentMonthGiftStats?.totalRevenueCompletedOrders || 0),
        previousMonthGiftAmount: Number(previousMonthGiftStats?.totalRevenueCompletedOrders || 0),
        currentMonthGiftCount: Number(currentMonthGiftStats?.totalOrders || 0) - Number(currentMonthGiftStats?.canceledOrders || 0),
        previousMonthGiftCount: Number(previousMonthGiftStats?.totalOrders || 0) - Number(previousMonthGiftStats?.canceledOrders || 0),
      });
    } finally {
      setIsGiftStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGiftProducts();
    loadGiftStats();
  }, [loadGiftProducts, loadGiftStats]);

  const productOptions = useMemo(() => giftProducts
    .filter((product) => {
      const provider = String(product.productProvider || "").toUpperCase();

      if (giftProductProvider === "ALL") {
        return GIFT_PRODUCT_ALLOWED_PROVIDERS.includes(provider);
      }

      return provider === String(giftProductProvider || "").toUpperCase();
    })
    .map((product) => ({
      label: getProductOptionLabel(product),
      value: product.productId,
    })), [giftProductProvider, giftProducts]);

  const handleExportGiftOrders = async () => {
    setIsExportProgress(true);
    setExportPercent(0);

    const pageSize = 100;
    let pageIndex = 0;
    let results = [];

    while (true) {
      const response = await getGiftOrders({
        filter: filters,
        order: sorter,
        ...(String(searchValue || "").trim() && { searchText: String(searchValue).trim() }),
        ...(startDate && { startDate: moment(startDate).set("HH", 0).set("mm", 0).set("ss", 0) }),
        ...(endDate && { endDate: moment(endDate).set("HH", 23).set("mm", 59).set("ss", 59) }),
        skip: pageIndex * pageSize,
        limit: pageSize,
      });
      const page = response?.data || [];
      if (!page.length) {
        break;
      }
      results = results.concat(page);
      pageIndex += 1;
      setExportPercent(Math.min(pageIndex * 20, 95));
    }

    const exportData = results.length > 0 ? results : data;
    const member = JSON.parse(window.localStorage.getItem(addKeyLocalStorage("data"))) || {};
    const stationCode = member.stationCode || member.stationsId || "ALL";
    const exportTime = moment().format("YYYYMMDD_HHmmss");

    onExportExcel({
      fieldApi: GIFT_EXPORT_FIELDS.map((field) => field.key),
      fieldExport: GIFT_EXPORT_FIELDS.map((field) => field.label),
      data: exportData.map((item) => {
      const productCategory = item?.orderItems?.[0]?.productCategory;

      return {
        ...item,
        giftAmount: number_to_price(getGiftAmount(item)),
        customerInfo: [
          item?.firstName,
          item?.phoneNumber,
          item?.email,
        ].filter(Boolean).join(" - "),
        giftType: PRODUCT_CATEGORIES[productCategory]?.label || "",
        giftPackage: item?.orderItems?.map((orderItem) => orderItem.orderItemName).filter(Boolean).join(", ") || "",
        orderStatusText: GIFT_ORDER_STATUS_TEXT[item?.orderStatus] || item?.orderStatus || "",
        createdAt: item?.createdAt ? moment(item.createdAt).format("DD/MM/YYYY HH:mm:ss") : "",
      };
    }),
        informationColumn: [],
      timeWait: 0,
      nameFile: `QuaTang_${stationCode}_${exportTime}.xlsx`,
    });

    setExportPercent(100);
    setTimeout(() => setIsExportProgress(false), 500);
  };

  const handleCloseGiftModal = () => {
    setIsGiftModalOpen(false);
    form.resetFields();
  };

  const handleCreateGift = async (values) => {
    const normalizedPlate = normalizePlate(values.licensePlates);
    setIsSubmitting(true);
    const result = await OrderService.createGiftOrder({
      firstName: String(values.firstName || "").trim(),
      phoneNumber: String(values.phoneNumber || "").trim(),
      email: String(values.email || "").trim(),
      productIds: [values.productId],
      licensePlatesList: [normalizedPlate],
    });
    setIsSubmitting(false);

    if (result?.statusCode !== 200) {
      notification.error({
        message: "Tặng quà thất bại",
        description: {
          UNCONFIRMED_BOOKING_EXISTED: "Xe đã có gói tương tự đang hoạt động",
        }[result?.error] || result?.error || "Vui lòng thử lại sau",
      });
      return;
    }

    notification.success({ message: "Tặng quà thành công" });
    handleCloseGiftModal();
    refetch();
    loadGiftStats();
  };

  const actionButtons = [
    <Button
      key="import-gift"
      icon={<UploadOutlined />}
      onClick={() => {
        setIsImportModalOpen(true);
        setImportModalVersion(prev => prev + 1);
      }}
      className="d-flex align-items-center gap-1"
    >
      Nhập file
    </Button>,
    <Button
      key="export-gift"
      icon={<DownloadOutlined />}
      onClick={handleExportGiftOrders}
      className="d-flex align-items-center gap-1"
    >
      Xuất file
    </Button>,
  ];

  return (
    <div>
      <GiftOrderStatsWidgets giftStats={giftStats} loading={isGiftStatsLoading} />
      <GiftOrderTable
        data={data}
        loading={loading}
        pagination={pagination}
        filters={filters}
        sorter={sorter}
        handleTableChange={handleTableChange}
        hasMore={hasMore}
        refetch={() => {
          refetch();
          loadGiftStats();
        }}
        handleSearch={handleSearch}
        startDate={startDate}
        endDate={endDate}
        onCreateGift={() => setIsGiftModalOpen(true)}
        actionButtons={actionButtons}
        giftProducts={giftProducts}
        columnStorageKey={addKeyLocalStorage(`gift-order-columns-${member?.stationsId || "ALL"}`)}
      />

      {isGiftModalOpen && (
        <Modal
          title="Tặng quà"
          visible={isGiftModalOpen}
          onCancel={handleCloseGiftModal}
          footer={[
            <Button key="cancel" onClick={handleCloseGiftModal}>
              Quay lại
            </Button>,
            <BasicButtonPrimary
              key="submit"
              onclick={() => form.submit()}
              loading={isSubmitting}
              text="Tặng quà"
            />,
          ]}
          destroyOnClose
          width={720}
        >
          <main>
            <div className="row">
              <div className="col-12">
                <Form form={form} layout="vertical" onFinish={handleCreateGift}>
                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item name="firstName" label="Tên khách hàng" rules={[{ required: true, message: "Vui lòng nhập tên khách hàng" }]}>
                        <Input placeholder="Nhập tên khách hàng" />
                      </Form.Item>
                    </Col>
                    <Col span={24} md={12}>
                      <Form.Item name="phoneNumber" label="Số điện thoại" rules={[{ required: true, validator: (_, value) => validateGiftField(validatePhoneNumber, value) }]}>
                        <Input placeholder="Nhập số điện thoại" />
                      </Form.Item>
                    </Col>
                    <Col span={24} md={12}>
                      <Form.Item name="email" label="Email" rules={[{ required: true, validator: (_, value) => validateGiftField(validateEmail, value) }]}>
                        <Input placeholder="Nhập email" />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        name="licensePlates"
                        label="Biển số"
                        rules={[
                          { required: true, message: "Vui lòng nhập biển số xe" },
                          {
                            validator: (_, value) => validateGiftField(validateVehicleIdentity, value),
                          },
                        ]}
                      >
                        <Input
                          placeholder="Nhập biển số"
                          onKeyDown={(event) => {
                            if (event.key === " ") {
                              event.preventDefault();
                            }
                          }}
                          onChange={(event) => {
                            form.setFieldsValue({
                              licensePlates: sanitizePlateInput(event.target.value),
                            });
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item label="Nhà cung cấp">
                        <Select
                          placeholder="Chọn nhà cung cấp"
                          options={GIFT_PRODUCT_PROVIDERS}
                          value={giftProductProvider}
                          onChange={(value) => {
                            setGiftProductProvider(value);
                            form.setFieldsValue({ productId: undefined });
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item name="productId" label="Tên quà tặng" rules={[{ required: true, message: "Vui lòng chọn tên quà tặng" }]}>
                        <Select
                          placeholder="Chọn tên quà tặng"
                          options={productOptions}
                          optionFilterProp="label"
                          dropdownClassName="gift-product-dropdown"
                          dropdownMatchSelectWidth={false}
                          showSearch
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              </div>
            </div>
          </main>
        </Modal>
      )}
      <GiftOrderImportModal
        key={importModalVersion}
        visible={isImportModalOpen}
        onCancel={() => setIsImportModalOpen(false)}
        onSuccess={() => {
          refetch();
          loadGiftStats();
        }}
        giftProducts={giftProducts}
      />
      <ModalProgress
        visible={isExportProgress}
        setVisible={setIsExportProgress}
        percent={exportPercent}
      />
    </div>
  );
};

export default GiftOrder;
